import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { useApplication } from '@eneco/common/application';
import RichText from '@eneco/components/RichText/RichText';
import { getEnecoBeXapiSiteApiV1OrdersByOrderId } from '@eneco/dc-be/client';
import usePublicDCBE from '@eneco/dc-be/hooks/usePublicDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { Button, Heading, RadioCard, RadioGroup, Stack, Text, Box } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { OrderManagementWithItems } from '@sitecore/types/manual/be/OrderManagementWithItems';
import { useRouter } from 'next/router';
import { Controller, useFormContext } from 'react-hook-form';
import { useSWRConfig } from 'swr';

import DataErrorNotification from '../../../components/DataErrorNotification';
import { ProposalRadioCard } from '../components/ProposalRadioCard';
import { OrderManagementContext } from '../contexts/OrderManagementProvider';
import { useOrderId } from '../hooks/useOrderId';
import { OrderManagementFormSchema } from '../types/orderManagementSchema';
import { FlowChildProps } from '../types/OrderManagementSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';
import { syncOrderBasketOrInitiate } from '../utils/syncOrderBasketOrInitiate';

export const ServiceProposalStep: FC<FlowChildProps> = ({ nextMainStep }) => {
  const { fields } = useContent<OrderManagementWithItems>();
  const { query } = useRouter();
  const { selectedAccount } = useSelfServiceAccount();
  const { language } = useApplication();
  const { setIsLoadingInitialOrder, isLoadingInitialOrder } = useContext(OrderManagementContext);
  const { trackEventBE } = useTracking();

  const [hasUpdateError, setHasUpdateError] = useState(false);

  const orderId = useOrderId();
  const priceSimulationId = query.priceSimulationId as string | undefined;
  const { mutate } = useSWRConfig();

  const { data: orderDataRaw } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1OrdersByOrderId,
    { path: { orderId: orderId ?? '' } },
    [`/orders/${orderId}`],
    !!orderId,
  );
  const orderData = unwrapData(orderDataRaw);

  const {
    control,
    trigger,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<OrderManagementFormSchema>();

  const serviceProposalOptions = useMemo(
    () => fields.items.filter(option => option.fields.data.isAssistanceProposalFlag?.value === true),
    [fields.items],
  );

  const basketServiceProposal = useMemo(() => {
    const optionNames = serviceProposalOptions.map(option => option.name);
    return orderData?.basket?.items?.find(
      item => item.orderProductType === 'NonCommodity' && optionNames.includes(item.technicalProductName ?? ''),
    )?.technicalProductName;
  }, [orderData?.basket?.items, serviceProposalOptions]);

  useEffect(() => {
    if (basketServiceProposal && !getValues('serviceProposal')) {
      setValue('serviceProposal', basketServiceProposal, { shouldValidate: false });
    }
  }, [basketServiceProposal, getValues, setValue]);

  const getSelectedServiceProposalPrice = (selectedValue: string | undefined): number => {
    if (!selectedValue || selectedValue === 'none') {
      return 0;
    }
    const option = serviceProposalOptions.find(({ name }) => name === selectedValue);
    return option?.fields.data.pricePerMonthVatExclusiveText?.value ?? 0;
  };

  const onNextClick = async () => {
    const isValid = await trigger('serviceProposal');
    if (!isValid) {
      return;
    }
    const serviceProposal = getValues('serviceProposal');
    const boilerMaintenance = getValues('boilerMaintenance');

    if (serviceProposal === 'none' && !basketServiceProposal && orderId) {
      trackEventBE('be_om_step_continue', {
        status: 'success',
        data: getTrackableFormValues(getValues(), {
          serviceProposalPrice: getSelectedServiceProposalPrice(serviceProposal),
        }),
      });
      nextMainStep();
      return;
    }

    setIsLoadingInitialOrder(true);
    try {
      const newOrderId = await syncOrderBasketOrInitiate({
        orderId,
        orderData,
        boilerMaintenance,
        serviceProposal,
        accountNumber: selectedAccount?.crmAccountNumber ?? '',
        language,
        priceSimulationId,
      });
      if (orderId) {
        mutate(`/orders/${orderId}`);
        mutate(`/orders/${orderId}/simulate`);
      }
      trackEventBE('be_om_step_continue', {
        status: 'success',
        data: getTrackableFormValues(getValues(), {
          serviceProposalPrice: getSelectedServiceProposalPrice(serviceProposal),
        }),
      });
      nextMainStep(undefined, newOrderId ?? undefined);
    } catch {
      setHasUpdateError(true);
      trackEventBE('be_om_step_continue', {
        status: 'failed',
        message: 'Failed to update order with selected service proposal',
        data: getTrackableFormValues(getValues()),
      });
      setIsLoadingInitialOrder(false);
    }
  };

  return (
    <>
      {hasUpdateError && <DataErrorNotification />}
      <Stack direction="column" gap="2">
        <Heading as="h1" size="M">
          {fields.assistanceService.title.value}
        </Heading>
        <div>
          <RichText html={fields.assistanceService.description.value} />
        </div>
      </Stack>
      <Controller
        control={control}
        name="serviceProposal"
        render={({ field }) => (
          <RadioGroup
            name="serviceProposal"
            aria-labelledby="serviceProposal"
            isFullWidth
            value={field.value}
            onValueChange={field.onChange}
            error={errors.serviceProposal?.message}>
            {serviceProposalOptions.map(option => {
              const {
                title,
                detailsLink,
                featuresContent,
                badgeLabel,
                priceLabel,
                taxLabel,
                pricePerMonthVatExclusiveText,
                pricePerMonthVatInclusiveText,
              } = option.fields.data;
              return (
                <ProposalRadioCard
                  key={option.name}
                  value={option.name}
                  title={title.value}
                  pricePerMonthVatExclusive={pricePerMonthVatExclusiveText?.value ?? 0}
                  pricePerMonthVatInclusive={pricePerMonthVatInclusiveText?.value ?? 0}
                  priceLabel={priceLabel.value}
                  taxLabel={taxLabel.value}
                  badgeLabel={badgeLabel.value}
                  featuresContent={featuresContent.value}
                  detailsLink={detailsLink.value}
                  type="serviceProposal"
                />
              );
            })}
            <RadioCard value="none" ariaLabelledby="none">
              <Box padding="6">
                <Stack direction="row" gap="2" alignY="center">
                  <RadioCard.Indicator />
                  <Text weight="bold" size="BodyL" id="none">
                    {fields.assistanceService.noneOfAboveLabel.value}
                  </Text>
                </Stack>
              </Box>
            </RadioCard>
          </RadioGroup>
        )}
      />
      <Button onClick={onNextClick} isLoading={isLoadingInitialOrder}>
        {fields.navigation.nextButtonLabel.value}
      </Button>
      <Text size="BodyXS" color="textLowEmphasis">
        <RichText html={fields.assistanceService.disclaimerContent.value} />
      </Text>
    </>
  );
};
